import React, { useState, useEffect, useCallback } from 'react';
import {
  getSystemLogs, getSystemLogsStats, exportSystemLogsJson, safeErrorMessage
} from '../services/api';
import { useToast } from '../App';
import Pagination from '../components/Pagination';
import {
  HiOutlineRefresh, HiOutlineDownload, HiOutlineFilter,
  HiOutlineClock, HiOutlineSearch,
} from 'react-icons/hi';
import { FiServer, FiShield, FiCpu, FiHardDrive, FiAlertTriangle, FiTrendingUp } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid, AreaChart, Area, PieChart, Pie, Cell } from 'recharts';

const LEVEL_STYLES = {
  INFO: { bg: '#dbeafe', color: '#1d4ed8' },
  WARNING: { bg: '#fef3c7', color: '#92400e' },
  ERROR: { bg: '#fee2e2', color: '#991b1b' },
  CRITICAL: { bg: '#fecaca', color: '#7f1d1d' },
};

const CATEGORY_LABELS = {
  auth: 'Authentification',
  admin: 'Administration',
  security: 'Securite',
  export: 'Export',
  ai: 'Intelligence Artificielle',
  system: 'Systeme',
};

const CATEGORY_COLORS = {
  auth: '#3b82f6',
  admin: '#8b5cf6',
  security: '#ef4444',
  export: '#06b6d4',
  ai: '#f59e0b',
  system: '#22c55e',
};

const LEVEL_LABELS = {
  INFO: 'Information', WARNING: 'Avertissement', ERROR: 'Erreur', CRITICAL: 'Critique',
};

const PIE_COLORS = ['#3b82f6', '#8b5cf6', '#ef4444', '#06b6d4', '#f59e0b', '#22c55e', '#ec4899', '#f97316'];

const ACTION_LABELS = {
  login_success: 'Connexion reussie',
  login_failed: 'Echec de connexion',
  login_inactive: 'Compte inactif',
  logout: 'Deconnexion',
  rate_limited: 'Rate limiting',
  password_changed: 'Changement mot de passe',
  user_created: 'Creation utilisateur',
  user_updated: 'Modification utilisateur',
  user_deleted: 'Suppression utilisateur',
};

const fmtDate = (d) => {
  const parts = d.split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}` : d;
};

const fmtDateFull = (d) => {
  try { return new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); }
  catch { return d; }
};

export default function SystemLogsPage() {
  const addToast = useToast();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(40);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);

  const [filters, setFilters] = useState({
    level: '', category: '', action: '', search: '', date_from: '', date_to: '',
  });
  const [statsDateFrom, setStatsDateFrom] = useState('');
  const [statsDateTo, setStatsDateTo] = useState('');
  const [activeTab, setActiveTab] = useState('logs'); // logs, security, system
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    date_from: '', date_to: '', category: '',
  });

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await getSystemLogs(params);
      setLogs(data.items);
      setTotal(data.total);
    } catch (err) {
      addToast('error', safeErrorMessage(err, 'Erreur chargement logs'));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters, addToast]);

  const loadStats = useCallback(async () => {
    try {
      const p = {};
      if (statsDateFrom) p.date_from = statsDateFrom;
      if (statsDateTo) p.date_to = statsDateTo;
      const { data } = await getSystemLogsStats(p);
      setStats(data);
    } catch { /* ignore */ }
  }, [statsDateFrom, statsDateTo]);

  useEffect(() => { loadLogs(); }, [loadLogs]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const openExportModal = () => {
    setExportOptions({
      date_from: statsDateFrom || filters.date_from || '',
      date_to: statsDateTo || filters.date_to || '',
      category: filters.category || '',
    });
    setShowExportModal(true);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (exportOptions.date_from) params.date_from = exportOptions.date_from;
      if (exportOptions.date_to) params.date_to = exportOptions.date_to;
      if (exportOptions.category) params.category = exportOptions.category;
      const { data } = await exportSystemLogsJson(params);
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      const suffix = exportOptions.date_from || exportOptions.date_to
        ? `${exportOptions.date_from || 'debut'}_${exportOptions.date_to || 'fin'}`
        : 'global';
      a.download = `system_logs_${suffix}_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      addToast('success', 'Export JSON telecharge');
      setShowExportModal(false);
    } catch (err) {
      addToast('error', safeErrorMessage(err, 'Erreur export'));
    } finally {
      setExporting(false);
    }
  };

  const resetFilters = () => {
    setFilters({ level: '', category: '', action: '', search: '', date_from: '', date_to: '' });
    setPage(1);
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  const parseDetails = (detailsStr) => {
    try {
      const d = JSON.parse(detailsStr);
      if (!d || typeof d !== 'object' || Object.keys(d).length === 0) return null;
      return d;
    } catch { return null; }
  };

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <h2>
          <FiServer style={{ marginRight: 8 }} />
          Logs Systeme
        </h2>
        <div className="actions">
          <button className="btn btn-outline-secondary btn-sm" onClick={() => { loadLogs(); loadStats(); }} title="Rafraichir">
            <HiOutlineRefresh />
          </button>
          <button className="btn btn-success btn-sm" onClick={openExportModal}>
            <HiOutlineDownload style={{ marginRight: 4 }} />
            Export JSON
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="audit-tabs mb-3">
        {[
          { key: 'logs', label: 'Tous les logs', icon: <FiServer /> },
          { key: 'stats', label: 'Statistiques', icon: <FiTrendingUp /> },
          { key: 'security', label: 'Securite', icon: <FiShield /> },
          { key: 'system', label: 'Metriques Serveur', icon: <FiCpu /> },
        ].map(tab => (
          <button
            key={tab.key}
            className={`audit-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab: Logs */}
      {activeTab === 'logs' && (
        <>
          {/* Filters */}
          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex align-items-center gap-2 mb-2">
                <HiOutlineFilter />
                <strong style={{ fontSize: '0.85rem' }}>Filtres</strong>
                <button className="btn btn-link btn-sm" onClick={resetFilters} style={{ fontSize: '0.75rem' }}>Reinitialiser</button>
              </div>
              <div className="row g-2">
                <div className="col-md-2">
                  <select className="form-select form-select-sm" value={filters.level}
                    onChange={e => { setFilters({ ...filters, level: e.target.value }); setPage(1); }}>
                    <option value="">Tous les niveaux</option>
                    <option value="INFO">INFO</option>
                    <option value="WARNING">WARNING</option>
                    <option value="ERROR">ERROR</option>
                    <option value="CRITICAL">CRITICAL</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <select className="form-select form-select-sm" value={filters.category}
                    onChange={e => { setFilters({ ...filters, category: e.target.value }); setPage(1); }}>
                    <option value="">Toutes categories</option>
                    {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="col-md-2">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text"><HiOutlineSearch /></span>
                    <input type="text" className="form-control" value={filters.search}
                      onChange={e => { setFilters({ ...filters, search: e.target.value }); setPage(1); }}
                      placeholder="Rechercher..." />
                  </div>
                </div>
                <div className="col-md-2">
                  <input type="date" className="form-control form-control-sm" value={filters.date_from}
                    onChange={e => { setFilters({ ...filters, date_from: e.target.value }); setPage(1); }} />
                </div>
                <div className="col-md-2">
                  <input type="date" className="form-control form-control-sm" value={filters.date_to}
                    onChange={e => { setFilters({ ...filters, date_to: e.target.value }); setPage(1); }} />
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-2">
            <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>
              {total} log{total > 1 ? 's' : ''}
            </span>
          </div>

          {/* Table */}
          <div className="card">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0" style={{ fontSize: '0.82rem' }}>
                  <thead>
                    <tr>
                      <th style={{ width: 80 }}>Niveau</th>
                      <th style={{ width: 150 }}>Date</th>
                      <th>Categorie</th>
                      <th>Action</th>
                      <th>Message</th>
                      <th>Utilisateur</th>
                      <th>IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={7} className="text-center py-4">
                        <output className="spinner-border spinner-border-sm" /> Chargement...
                      </td></tr>
                    ) : logs.length === 0 ? (
                      <tr><td colSpan={7} className="text-center py-4 text-muted">Aucun log</td></tr>
                    ) : logs.map(l => {
                      const lvl = LEVEL_STYLES[l.level] || LEVEL_STYLES.INFO;
                      const details = parseDetails(l.details);
                      return (
                        <React.Fragment key={l.id}>
                          <tr
                            style={{ cursor: details ? 'pointer' : 'default' }}
                            onClick={() => details && setExpandedLog(expandedLog === l.id ? null : l.id)}
                          >
                            <td>
                              <span className="syslog-level-badge" style={{ backgroundColor: lvl.bg, color: lvl.color }}>
                                {l.level}
                              </span>
                            </td>
                            <td style={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                              <HiOutlineClock style={{ marginRight: 3 }} />
                              {formatDate(l.created_at)}
                            </td>
                            <td>
                              <span className="badge bg-light text-dark" style={{ fontSize: '0.7rem' }}>
                                {CATEGORY_LABELS[l.category] || l.category}
                              </span>
                            </td>
                            <td className="fw-500">{l.action}</td>
                            <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {l.message}
                            </td>
                            <td>{l.username || '-'}</td>
                            <td style={{ fontSize: '0.72rem', fontFamily: 'monospace' }}>{l.ip_address || '-'}</td>
                          </tr>
                          {expandedLog === l.id && details && (
                            <tr>
                              <td colSpan={7} style={{ backgroundColor: 'var(--gray-50)', padding: '0.75rem 1rem' }}>
                                <div style={{ fontSize: '0.78rem' }}>
                                  <strong>Details :</strong>
                                  <pre style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                                    {JSON.stringify(details, null, 2)}
                                  </pre>
                                  {l.user_agent && (
                                    <div className="mt-1" style={{ color: 'var(--gray-400)', fontSize: '0.7rem' }}>
                                      <strong>User-Agent :</strong> {l.user_agent}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {total > pageSize && (
            <div className="mt-3">
              <Pagination currentPage={page} totalItems={total} pageSize={pageSize} onPageChange={setPage} />
            </div>
          )}
        </>
      )}

      {/* Tab: Statistiques */}
      {activeTab === 'stats' && !stats && (
        <div className="text-center py-4"><output className="spinner-border spinner-border-sm" /> Chargement des statistiques...</div>
      )}
      {activeTab === 'stats' && stats && (
        <div>
          {/* Periode selector */}
          <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
            <label className="form-label mb-0" style={{ fontSize: '0.82rem' }}>Periode :</label>
            <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }}
              value={statsDateFrom} onChange={e => setStatsDateFrom(e.target.value)} />
            <span style={{ fontSize: '0.82rem' }}>a</span>
            <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }}
              value={statsDateTo} onChange={e => setStatsDateTo(e.target.value)} />
            {(statsDateFrom || statsDateTo) && (
              <button className="btn btn-outline-secondary btn-sm" onClick={() => { setStatsDateFrom(''); setStatsDateTo(''); }}>Tout</button>
            )}
          </div>

          {/* KPI Cards */}
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <div className="card audit-stat-card">
                <div className="card-body text-center">
                  <div className="audit-stat-value">{stats.total_events || 0}</div>
                  <div className="audit-stat-label">Evenements totaux</div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card audit-stat-card">
                <div className="card-body text-center">
                  <div className="audit-stat-value" style={{ color: '#22c55e' }}>
                    {stats.security?.successful_logins || 0}
                  </div>
                  <div className="audit-stat-label">Connexions reussies</div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card audit-stat-card">
                <div className="card-body text-center">
                  <div className="audit-stat-value" style={{ color: '#ef4444' }}>
                    {stats.security?.failed_logins || 0}
                  </div>
                  <div className="audit-stat-label">Echecs de connexion</div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card audit-stat-card">
                <div className="card-body text-center">
                  <div className="audit-stat-value" style={{ color: '#f59e0b' }}>
                    {stats.security?.rate_limited || 0}
                  </div>
                  <div className="audit-stat-label">Requetes bloquees</div>
                </div>
              </div>
            </div>
          </div>

          {/* Activite par jour et categorie (barres empilees) */}
          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Activite journaliere par categorie</h6>
                <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                  {(stats.daily_by_category || []).length} jour(s) d'activite
                </span>
              </div>
              {(stats.daily_by_category || []).length === 0 ? (
                <div className="text-center text-muted py-4" style={{ fontSize: '0.82rem' }}>Aucune activite sur cette periode</div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats.daily_by_category || []} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={fmtDate} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} label={{ value: 'Nombre', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#888' } }} />
                    <Tooltip labelFormatter={fmtDateFull} formatter={(v, name) => [v + ' evenement(s)', CATEGORY_LABELS[name] || name]} contentStyle={{ fontSize: '0.78rem' }} />
                    <Legend formatter={v => CATEGORY_LABELS[v] || v} wrapperStyle={{ fontSize: '0.75rem' }} />
                    {Object.keys(CATEGORY_COLORS).map(cat => (
                      <Bar key={cat} dataKey={cat} stackId="a" fill={CATEGORY_COLORS[cat]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Tendance + Repartition par niveau */}
          <div className="row g-3 mb-3">
            <div className="col-md-7">
              <div className="card h-100">
                <div className="card-body">
                  <h6 className="mb-3">Tendance d'activite (total/jour)</h6>
                  {(stats.daily_total || []).length === 0 ? (
                    <div className="text-center text-muted py-4" style={{ fontSize: '0.82rem' }}>Aucune donnee</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={240}>
                      <AreaChart data={stats.daily_total || []} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={fmtDate} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip labelFormatter={fmtDateFull} formatter={(v) => [v, 'Evenements']} contentStyle={{ fontSize: '0.78rem' }} />
                        <Area type="monotone" dataKey="total" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.15} strokeWidth={2} name="Evenements" />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-5">
              <div className="card h-100">
                <div className="card-body">
                  <h6 className="mb-3">Repartition par niveau de severite</h6>
                  {Object.keys(stats.by_level || {}).length === 0 ? (
                    <div className="text-center text-muted py-5" style={{ fontSize: '0.82rem' }}>Aucune donnee</div>
                  ) : (
                    <>
                      <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                          <Pie
                            data={['INFO', 'WARNING', 'ERROR', 'CRITICAL'].filter(l => stats.by_level?.[l]).map(l => ({ name: LEVEL_LABELS[l], value: stats.by_level[l], level: l }))}
                            dataKey="value" nameKey="name"
                            cx="50%" cy="50%" innerRadius={45} outerRadius={70}
                          >
                            {['INFO', 'WARNING', 'ERROR', 'CRITICAL'].filter(l => stats.by_level?.[l]).map(l => (
                              <Cell key={l} fill={LEVEL_STYLES[l].color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v, name) => [v + ' evenement(s)', name]} contentStyle={{ fontSize: '0.78rem' }} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="mt-1">
                        {['INFO', 'WARNING', 'ERROR', 'CRITICAL'].filter(l => stats.by_level?.[l]).map(l => (
                          <div key={l} className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: '0.78rem' }}>
                            <span>
                              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, backgroundColor: LEVEL_STYLES[l].color, marginRight: 6 }} />
                              {LEVEL_LABELS[l]}
                            </span>
                            <strong style={{ color: LEVEL_STYLES[l].color }}>{stats.by_level[l]}</strong>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Repartition categories + Top actions */}
          <div className="row g-3">
            <div className="col-md-6">
              <div className="card h-100">
                <div className="card-body">
                  <h6 className="mb-3">Repartition par categorie</h6>
                  {Object.keys(stats.by_category || {}).length === 0 ? (
                    <div className="text-center text-muted py-3" style={{ fontSize: '0.82rem' }}>Aucune donnee</div>
                  ) : (
                    <div>
                      {Object.entries(stats.by_category || {}).sort((a, b) => b[1] - a[1]).map(([cat, cnt]) => {
                        const totalEvts = Object.values(stats.by_category).reduce((a, b) => a + b, 0);
                        const pct = totalEvts > 0 ? Math.round((cnt / totalEvts) * 100) : 0;
                        return (
                          <div key={cat} className="mb-2">
                            <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.8rem' }}>
                              <span>
                                <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, backgroundColor: CATEGORY_COLORS[cat] || '#6b7a90', marginRight: 6 }} />
                                <span className="fw-500">{CATEGORY_LABELS[cat] || cat}</span>
                              </span>
                              <span style={{ color: 'var(--gray-500)' }}>{cnt} — {pct}%</span>
                            </div>
                            <div className="progress" style={{ height: 6, borderRadius: 3 }}>
                              <div className="progress-bar" role="progressbar"
                                style={{ width: `${pct}%`, backgroundColor: CATEGORY_COLORS[cat] || '#6b7a90', borderRadius: 3 }}
                                aria-valuenow={pct} aria-valuemin="0" aria-valuemax="100" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card h-100">
                <div className="card-body">
                  <h6 className="mb-3">Actions les plus frequentes</h6>
                  {(stats.top_actions || []).length === 0 ? (
                    <div className="text-center text-muted py-3" style={{ fontSize: '0.82rem' }}>Aucune donnee</div>
                  ) : (
                    <div>
                      {(stats.top_actions || []).map((a, i) => {
                        const maxCount = stats.top_actions[0]?.count || 1;
                        const pct = Math.round((a.count / maxCount) * 100);
                        return (
                          <div key={i} className="mb-2">
                            <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.8rem' }}>
                              <span className="fw-500">{ACTION_LABELS[a.action] || a.action}</span>
                              <span style={{ color: 'var(--gray-500)' }}>{a.count} fois</span>
                            </div>
                            <div className="progress" style={{ height: 6, borderRadius: 3 }}>
                              <div className="progress-bar" role="progressbar"
                                style={{ width: `${pct}%`, backgroundColor: PIE_COLORS[i % PIE_COLORS.length], borderRadius: 3 }}
                                aria-valuenow={pct} aria-valuemin="0" aria-valuemax="100" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Security */}
      {activeTab === 'security' && !stats && (
        <div className="text-center py-4"><output className="spinner-border spinner-border-sm" /> Chargement...</div>
      )}
      {activeTab === 'security' && stats && (
        <div>
          {/* Periode */}
          <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
            <label className="form-label mb-0" style={{ fontSize: '0.82rem' }}>Periode :</label>
            <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }}
              value={statsDateFrom} onChange={e => setStatsDateFrom(e.target.value)} />
            <span style={{ fontSize: '0.82rem' }}>a</span>
            <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }}
              value={statsDateTo} onChange={e => setStatsDateTo(e.target.value)} />
            {(statsDateFrom || statsDateTo) && (
              <button className="btn btn-outline-secondary btn-sm" onClick={() => { setStatsDateFrom(''); setStatsDateTo(''); }}>Tout</button>
            )}
          </div>

          {/* KPI Cards */}
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <div className="card audit-stat-card">
                <div className="card-body text-center">
                  <div className="audit-stat-value" style={{ color: '#22c55e' }}>
                    {stats.security?.successful_logins || 0}
                  </div>
                  <div className="audit-stat-label">Connexions reussies</div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card audit-stat-card">
                <div className="card-body text-center">
                  <div className="audit-stat-value" style={{ color: '#ef4444' }}>
                    {stats.security?.failed_logins || 0}
                  </div>
                  <div className="audit-stat-label">Echecs de connexion</div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card audit-stat-card">
                <div className="card-body text-center">
                  <div className="audit-stat-value" style={{ color: '#f59e0b' }}>
                    {stats.security?.rate_limited || 0}
                  </div>
                  <div className="audit-stat-label">Requetes bloquees</div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card audit-stat-card">
                <div className="card-body text-center">
                  <div className="audit-stat-value">
                    {Object.values(stats.by_level || {}).reduce((a, b) => a + b, 0)}
                  </div>
                  <div className="audit-stat-label">Total evenements</div>
                </div>
              </div>
            </div>
          </div>

          {/* By level cards */}
          <div className="row g-3 mb-3">
            {['INFO', 'WARNING', 'ERROR', 'CRITICAL'].map(lvl => (
              <div className="col-md-3" key={lvl}>
                <div className="card" style={{ borderLeft: `3px solid ${LEVEL_STYLES[lvl].color}` }}>
                  <div className="card-body d-flex justify-content-between align-items-center py-2">
                    <span style={{ fontSize: '0.82rem', fontWeight: 500 }}>{LEVEL_LABELS[lvl]}</span>
                    <strong style={{ color: LEVEL_STYLES[lvl].color }}>{stats.by_level?.[lvl] || 0}</strong>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Timeline severite */}
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="mb-3">Evenements par jour et niveau de severite</h6>
              {(stats.daily_timeline || []).length === 0 ? (
                <div className="text-center text-muted py-4" style={{ fontSize: '0.82rem' }}>Aucun evenement sur cette periode</div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats.daily_timeline || []} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={fmtDate} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} label={{ value: 'Nombre', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#888' } }} />
                    <Tooltip labelFormatter={fmtDateFull} formatter={(v, name) => [v + ' evenement(s)', name]} contentStyle={{ fontSize: '0.78rem' }} />
                    <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                    <Bar dataKey="INFO" name="Information" stackId="a" fill="#3b82f6" />
                    <Bar dataKey="WARNING" name="Avertissement" stackId="a" fill="#f59e0b" />
                    <Bar dataKey="ERROR" name="Erreur" stackId="a" fill="#ef4444" />
                    <Bar dataKey="CRITICAL" name="Critique" stackId="a" fill="#7f1d1d" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tab: System metrics */}
      {activeTab === 'system' && stats && (
        <div>
          <div className="d-flex align-items-center gap-2 mb-3">
            <FiCpu />
            <strong style={{ fontSize: '0.85rem' }}>Metriques serveur</strong>
            <span className="text-muted" style={{ fontSize: '0.75rem' }}>(temps reel)</span>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <div className="card audit-stat-card">
                <div className="card-body text-center">
                  <FiCpu size={24} style={{ marginBottom: 8, color: 'var(--navy-500)' }} />
                  <div className="audit-stat-value">
                    {stats.system?.cpu_percent !== undefined ? `${stats.system.cpu_percent}%` : 'N/A'}
                  </div>
                  <div className="audit-stat-label">CPU</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--gray-400)' }}>{stats.system?.cpu_count || '?'} cores</div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card audit-stat-card">
                <div className="card-body text-center">
                  <FiServer size={24} style={{ marginBottom: 8, color: 'var(--navy-500)' }} />
                  <div className="audit-stat-value">
                    {stats.system?.ram_percent !== undefined ? `${stats.system.ram_percent}%` : 'N/A'}
                  </div>
                  <div className="audit-stat-label">RAM</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--gray-400)' }}>
                    {stats.system?.ram_used_gb !== undefined
                      ? `${stats.system.ram_used_gb} / ${stats.system.ram_total_gb} GB`
                      : '-'}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card audit-stat-card">
                <div className="card-body text-center">
                  <FiHardDrive size={24} style={{ marginBottom: 8, color: 'var(--navy-500)' }} />
                  <div className="audit-stat-value">
                    {stats.system?.disk_percent !== undefined ? `${stats.system.disk_percent}%` : 'N/A'}
                  </div>
                  <div className="audit-stat-label">Disque</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--gray-400)' }}>
                    {stats.system?.disk_used_gb !== undefined
                      ? `${stats.system.disk_used_gb} / ${stats.system.disk_total_gb} GB`
                      : '-'}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card audit-stat-card">
                <div className="card-body text-center">
                  <FiAlertTriangle size={24} style={{ marginBottom: 8, color: 'var(--navy-500)' }} />
                  <div className="audit-stat-value">{stats.system?.platform || '-'}</div>
                  <div className="audit-stat-label">Plateforme</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--gray-400)' }}>
                    Python {stats.system?.python_version || '?'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {stats.system?.note && (
            <div className="alert alert-warning" style={{ fontSize: '0.82rem' }}>
              <FiAlertTriangle style={{ marginRight: 6 }} />
              {stats.system.note}
              <br />
              <code style={{ fontSize: '0.75rem' }}>pip install psutil</code> pour activer les metriques CPU/RAM/Disque.
            </div>
          )}

          {/* Resource usage bars */}
          {stats.system?.cpu_percent !== undefined && (
            <div className="card">
              <div className="card-body">
                <h6 className="mb-3">Utilisation des ressources</h6>
                {[
                  { label: 'CPU', value: stats.system.cpu_percent, color: stats.system.cpu_percent > 80 ? '#ef4444' : '#3b82f6' },
                  { label: 'RAM', value: stats.system.ram_percent, color: stats.system.ram_percent > 80 ? '#ef4444' : '#22c55e' },
                  { label: 'Disque', value: stats.system.disk_percent, color: stats.system.disk_percent > 85 ? '#ef4444' : '#f59e0b' },
                ].map(r => (
                  <div key={r.label} className="mb-3">
                    <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.82rem' }}>
                      <span>{r.label}</span>
                      <strong>{r.value}%</strong>
                    </div>
                    <div className="progress" style={{ height: 10, borderRadius: 5 }}>
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${r.value}%`, backgroundColor: r.color, borderRadius: 5 }}
                        aria-valuenow={r.value} aria-valuemin="0" aria-valuemax="100"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" style={{ fontSize: '1rem' }}>
                  <HiOutlineDownload style={{ marginRight: 6 }} />
                  Export JSON — Logs Systeme
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowExportModal(false)} aria-label="Fermer" />
              </div>
              <div className="modal-body">
                <p style={{ fontSize: '0.82rem', color: 'var(--gray-500)' }}>
                  Configurez les parametres de l'export JSON des logs systeme.
                  Par defaut, l'export contient tous les logs (export global).
                </p>

                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: '0.82rem', fontWeight: 500 }}>Periode</label>
                  <div className="d-flex align-items-center gap-2">
                    <input type="date" className="form-control form-control-sm"
                      value={exportOptions.date_from}
                      onChange={e => setExportOptions({ ...exportOptions, date_from: e.target.value })} />
                    <span style={{ fontSize: '0.82rem' }}>a</span>
                    <input type="date" className="form-control form-control-sm"
                      value={exportOptions.date_to}
                      onChange={e => setExportOptions({ ...exportOptions, date_to: e.target.value })} />
                  </div>
                  <div className="form-text" style={{ fontSize: '0.72rem' }}>
                    Laisser vide pour exporter tous les logs
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: '0.82rem', fontWeight: 500 }}>Categorie</label>
                  <select className="form-select form-select-sm"
                    value={exportOptions.category}
                    onChange={e => setExportOptions({ ...exportOptions, category: e.target.value })}>
                    <option value="">Toutes les categories (global)</option>
                    {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>

                <div className="alert alert-light mt-3 mb-0" style={{ fontSize: '0.75rem', border: '1px solid var(--gray-200)' }}>
                  <strong>Format :</strong> JSON structure — compatible avec les outils d'audit de conformite.
                  {!exportOptions.date_from && !exportOptions.date_to && !exportOptions.category && (
                    <span className="d-block mt-1 text-primary fw-500">Export global (tous les logs systeme)</span>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowExportModal(false)}>
                  Annuler
                </button>
                <button type="button" className="btn btn-success btn-sm" onClick={handleExport} disabled={exporting}>
                  <HiOutlineDownload style={{ marginRight: 4 }} />
                  {exporting ? 'Telechargement...' : 'Telecharger JSON'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
